import Quill, { Delta } from 'quill';
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { CaregiverPhraseDto } from '@/caregiverSpace/shared/model';

import { useLogger } from '../log';

export type SlashMenuPosition = {
    top: number;
    left: number;
};

export type SlashMenuState = {
    isOpen: boolean;
    position: SlashMenuPosition;
    filteredPhrases: CaregiverPhraseDto[];
    selectedIndex: number;
};

type UseSlashMenuOptions = {
    quillRef: MutableRefObject<Quill | null>;
    phrases: CaregiverPhraseDto[] | undefined;
    onPhraseSelect?: (phrase: CaregiverPhraseDto) => string;
};

type UseSlashMenuReturn = {
    slashMenuState: SlashMenuState;
    handleSelect: (template: CaregiverPhraseDto) => void;
    handleClose: () => void;
    openViaButton: (buttonEl: HTMLElement) => void;
};

// Computes the cursor index after a change by walking the delta ops.
// quill.getSelection() can return a stale position inside the text-change handler
// because Quill updates selection asynchronously relative to the text-change event.
const getCursorIndexFromDelta = (delta: Delta): number => {
    let index = 0;
    for (const op of delta.ops ?? []) {
        if (typeof op.retain === 'number') index += op.retain;
        else if (op.insert !== undefined) {
            index += typeof op.insert === 'string' ? op.insert.length : 1;
        }
        // delete ops don't advance the cursor
    }
    return index;
};

const getCursorViewportPosition = (
    quill: Quill,
    cursorIndex: number,
): SlashMenuPosition | null => {
    const bounds = quill.getBounds(cursorIndex);
    if (!bounds) return null;
    // getBounds returns coordinates relative to quill.container; add the
    // container's viewport offset to get fixed-position coordinates.
    const containerRect = quill.container.getBoundingClientRect();
    return {
        top: containerRect.top + bounds.bottom,
        left: containerRect.left + bounds.left,
    };
};

// Matches a slash followed by zero or more non-whitespace, non-slash characters at the end of the string.
// The slash must be at the start of the string, after a newline, a space, or a tab — so dates like 31/05 don't trigger the menu.
const SLASH_PATTERN = /(^|[\n \t])\/([^\s/]*)$/;

export const CURSOR_PLACEHOLDER = 'CURSOR_PLACEHOLDER';

export const useSlashMenu = ({
    quillRef,
    phrases,
    onPhraseSelect,
}: UseSlashMenuOptions): UseSlashMenuReturn => {
    const [state, setState] = useState<SlashMenuState>({
        isOpen: false,
        position: { top: 0, left: 0 },
        filteredPhrases: [],
        selectedIndex: 0,
    });

    // Refs allow event handlers set up once to always read the latest values.
    const stateRef = useRef(state);
    stateRef.current = state;

    const phrasesRef = useRef(phrases);
    phrasesRef.current = phrases;

    const onPhraseSelectRef = useRef(onPhraseSelect);
    onPhraseSelectRef.current = onPhraseSelect;

    const slashIndexRef = useRef<number | null>(null);
    const triggerSourceRef = useRef<'slash' | 'button'>('slash');

    const log = useLogger();

    const close = useCallback(() => {
        slashIndexRef.current = null;
        setState((prev) => (prev.isOpen ? { ...prev, isOpen: false } : prev));
    }, []);

    const openViaButton = useCallback((buttonEl: HTMLElement) => {
        const quill = quillRef.current;
        const currentPhrases = phrasesRef.current;
        if (!quill || !currentPhrases || currentPhrases.length === 0) return;

        triggerSourceRef.current = 'button';
        log('Phrases menu opened via toolbar button');

        const selection = quill.getSelection();
        const index = selection?.index ?? quill.getLength() - 1;
        slashIndexRef.current = index;

        const rect = buttonEl.getBoundingClientRect();
        const position: SlashMenuPosition = {
            top: rect.bottom,
            left: rect.left,
        };

        setState({
            isOpen: true,
            position,
            filteredPhrases: currentPhrases,
            selectedIndex: 0,
        });
    }, []);

    const handleSelect = useCallback(
        (phrase: CaregiverPhraseDto) => {
            const quill = quillRef.current;
            const slashIndex = slashIndexRef.current;
            if (!quill || slashIndex === null) return;

            const selection = quill.getSelection();
            const cursorIndex = selection?.index ?? slashIndex;
            const deleteLength = cursorIndex - slashIndex;

            quill.deleteText(slashIndex, deleteLength, 'api');

            const content =
                onPhraseSelectRef.current?.(phrase) ?? phrase.Content;

            const lengthAfterDeletion = quill.getLength();
            quill.clipboard.dangerouslyPasteHTML(slashIndex, content, 'api');
            const insertedLength = quill.getLength() - lengthAfterDeletion;

            // Walk the inserted delta ops (not the raw HTML) to find the
            // cursorPlaceholder embed, so it's found regardless of surrounding
            // markup, then remove it as a single unit.
            const insertedOps = quill.getContents(
                slashIndex,
                insertedLength,
            ).ops;
            let cursorPosition = slashIndex + insertedLength;
            let offset = slashIndex;
            let placeholderRemoved = false;
            for (const op of insertedOps ?? []) {
                const opLength =
                    typeof op.insert === 'string' ? op.insert.length : 1;
                if (
                    op.insert &&
                    typeof op.insert === 'object' &&
                    'cursorPlaceholder' in op.insert
                ) {
                    quill.deleteText(offset, opLength, 'api');
                    cursorPosition = offset;
                    placeholderRemoved = true;
                    break;
                }
                offset += opLength;
            }

            // Plaintext-mode content has no embed — the placeholder is a literal
            // marker string instead, so fall back to a plain text search.
            if (!placeholderRemoved) {
                const insertedText = quill.getText(slashIndex, insertedLength);
                const placeholderIndex =
                    insertedText.indexOf(CURSOR_PLACEHOLDER);
                if (placeholderIndex !== -1) {
                    const deleteAt = slashIndex + placeholderIndex;
                    quill.deleteText(
                        deleteAt,
                        CURSOR_PLACEHOLDER.length,
                        'api',
                    );
                    cursorPosition = deleteAt;
                }
            }

            quill.setSelection(cursorPosition, 0, 'api');
            quill.focus();

            log('Phrase inserted', {
                phraseTitle: phrase.Title,
                trigger: triggerSourceRef.current,
            });

            close();
        },
        [quillRef, close],
    );

    // Text-change listener: detect slash and update menu state.
    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;

        const handleTextChange = (
            delta: Delta,
            _oldDelta: unknown,
            source: string,
        ) => {
            if (source !== 'user') return;

            const currentPhrases = phrasesRef.current;
            if (!currentPhrases) {
                if (stateRef.current.isOpen) close();
                return;
            }

            // Derive cursor index from the delta — quill.getSelection() lags behind
            // during the text-change event and returns the pre-change position.
            const cursorIndex = getCursorIndexFromDelta(delta);

            const text = quill.getText(0, cursorIndex);
            const match = SLASH_PATTERN.exec(text);

            if (!match) {
                if (stateRef.current.isOpen) close();
                return;
            }

            const searchText = match[2] ?? '';
            // match[1] is the character before the slash (empty string, space, or newline);
            // the slash index excludes that preceding character.
            slashIndexRef.current =
                cursorIndex - match[0].length + match[1].length;

            const filtered =
                searchText === ''
                    ? currentPhrases
                    : currentPhrases.filter((p) =>
                          p.Title.toLowerCase().includes(
                              searchText.toLowerCase(),
                          ),
                      );

            const position = getCursorViewportPosition(quill, cursorIndex) ?? {
                top: 0,
                left: 0,
            };

            if (!stateRef.current.isOpen) {
                triggerSourceRef.current = 'slash';
                log('Phrases menu opened via slash command');
            }

            setState({
                isOpen: true,
                position,
                filteredPhrases: filtered,
                selectedIndex: 0,
            });
        };

        quill.on('text-change', handleTextChange);
        return () => {
            quill.off('text-change', handleTextChange);
        };
    }, []);

    // Keyboard listener: navigate with arrow keys, confirm with Enter, dismiss with Escape.
    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (!stateRef.current.isOpen) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setState((prev) => ({
                    ...prev,
                    selectedIndex: Math.min(
                        prev.selectedIndex + 1,
                        prev.filteredPhrases.length - 1,
                    ),
                }));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setState((prev) => ({
                    ...prev,
                    selectedIndex: Math.max(prev.selectedIndex - 1, 0),
                }));
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                const { filteredPhrases, selectedIndex } = stateRef.current;
                const phrase = filteredPhrases[selectedIndex];
                if (phrase) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(phrase);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                close();
            }
        };

        // Capture phase so we intercept before Quill's own keyboard bindings.
        quill.root.addEventListener('keydown', handleKeyDown, true);
        return () => {
            quill.root.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [handleSelect, close]);

    return {
        slashMenuState: state,
        handleSelect,
        handleClose: close,
        openViaButton,
    };
};
